import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ApiService } from 'app/services/api.service';
import { MatSelectModule } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { ReplaySubject, Subject, takeUntil, forkJoin } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatOptionModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { NgxCaptchaModule, ReCaptcha2Component } from 'ngx-captcha';
import { PoliticasUsoPopupComponent } from './politicas-uso/politicas-uso-popup.component';
import { environment } from 'environments/environment';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

interface Device {
  descripcion: string;
  fabricante: string;
  id: number;
  ip: string;
  modelo: string;
  nombre: string;
  selectLabel: string;
}

interface Command {
  comando: string;
  descripcion: string;
  general_cisco: string;
  general_juniper: string;
  id: number;
  selectLabel: string;
}

@Component({
  selector: 'app-looking-glass',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatToolbarModule,
    MatFormFieldModule,
    MatProgressBarModule,
    MatPaginatorModule,
    MatTableModule,
    MatSortModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
    MatTooltipModule,
    MatSelectModule,
    MatOptionModule,
    MatCheckboxModule,
    NgxCaptchaModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './inicio.component.html',
  styleUrls: ['./inicio.component.scss'],
})
export class InicioComponent implements OnInit, OnDestroy {
  public isLoading: boolean = true;
  public apiOutput: string | null = null;

  form?: FormGroup;
  dynamicInputLabel: string = '';

  public siteKey: string = environment.recaptcha_public_key;

  devices: Device[] = [];
  public devicesFiltroCtrl: FormControl = new FormControl();
  public devicesFiltrados: ReplaySubject<Device[]> = new ReplaySubject<Device[]>(1);

  commands: Command[] = [];
  public commandsFiltroCtrl: FormControl = new FormControl();
  public commandsFiltrados: ReplaySubject<Command[]> = new ReplaySubject<Command[]>(1);

  private _onDestroy = new Subject<void>();

  @ViewChild(ReCaptcha2Component) captchaRef!: ReCaptcha2Component;

  constructor(
    readonly dataStorage: ApiService,
    readonly dialog: MatDialog,
    readonly router: Router,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    // Validaciones formulario
    this.form = this.fb.group({
      device: ['', Validators.required],
      command: ['', Validators.required],
      dynamicInput: [''],
      termsAccepted: [false, Validators.requiredTrue],
      recaptcha: ['', Validators.required],
    });

    this.loadData();

    // Filtros de los selects
    this.devicesFiltroCtrl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => {
      this.filtrarDevices();
    });

    this.commandsFiltroCtrl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => {
      this.filtrarCommands();
    });

    // Escucha cambios en command para actualizar el input dinámico y su etiqueta
    this.form
      ?.get('command')
      ?.valueChanges.pipe(takeUntil(this._onDestroy))
      .subscribe((commandId: number) => {
        // Busca el comando seleccionado por 'id'
        const selectedCommand = this.commands.find(cmd => cmd.id === commandId);
        this.updateDynamicInput(selectedCommand ? selectedCommand.selectLabel : null);
      });
  }

  // Carga los dispositivos y comandos.
  loadData(): void {
    this.isLoading = true;
    forkJoin({
      devices: this.dataStorage.getDevices(),
      commands: this.dataStorage.getCommands(),
    })
      .pipe(takeUntil(this._onDestroy))
      .subscribe({
        next: ({ devices, commands }) => {
          // Procesar dispositivos: Etiqueta = descripcion
          this.devices = devices.map((d: any): Device => ({
            ...d,
            selectLabel: d.descripcion,
          }));
          this.devicesFiltrados.next(this.devices.slice());

          // Procesar comandos: Etiqueta = comando - descripcion
          this.commands = commands.map((c: any): Command => ({
            ...c,
            selectLabel: `${c.comando} - ${c.descripcion}`,
          }));
          this.commandsFiltrados.next(this.commands.slice());

          this.isLoading = false;
        },
        error: error => {
          console.error('Error al cargar dispositivos o comandos:', error);
          this.isLoading = false;
        },
      });
  }

  // Construye el comando y llama al endpoint ejecutar_comando.
  ejecutarComando(): void {
    if (this.form && this.form.valid) {
      this.isLoading = true;
      this.apiOutput = null;

      const deviceId = this.form.get('device')?.value;
      const commandId = this.form.get('command')?.value;
      const dynamicInputValue = this.form.get('dynamicInput')?.value || '';

      const recaptchaToken = this.form.get('recaptcha')?.value;

      const selectedDevice = this.devices.find(d => d.id === deviceId);
      const selectedCommand = this.commands.find(c => c.id === commandId);

      if (!selectedDevice || !selectedCommand) {
        console.error('Dispositivo o Comando no encontrado.');
        this.isLoading = false;
        return;
      }

      const idMaquina = selectedDevice.id;

      const data = {
        id_maquina: idMaquina,
        id_comando: selectedCommand.id,
        valor: dynamicInputValue.trim(),
        recaptcha_token: recaptchaToken
      };

      this.dataStorage.executeCommand(data).pipe(takeUntil(this._onDestroy)).subscribe({
        next: (response) => {
          this.apiOutput = response.output || 'No response.';
          this.isLoading = false;

          if (this.captchaRef && this.captchaRef.resetCaptcha) {
            this.captchaRef.resetCaptcha();
          }
        },
        error: (error) => {
          if (error.status === 504){
            var mensaje = 'Timeout error'
          }else {
            var mensaje = 'Error en la ejecución'
          }
          
          this.apiOutput = error.error?.['error'] ?? mensaje;
          this.isLoading = false;

          if (this.captchaRef && this.captchaRef.resetCaptcha) {
            this.captchaRef.resetCaptcha();
          }
        }
      });
    } else {
      console.warn('Formulario inválido. No se puede ejecutar el comando.');
    }
  }

  resetearFormulario(): void {
    this.apiOutput = null;

    this.form?.reset({
      device: '',
      command: '',
      dynamicInput: '',
      termsAccepted: false,
      recaptcha: '',
    });

    this.devicesFiltroCtrl.setValue('');
    this.commandsFiltroCtrl.setValue('');
    this.updateDynamicInput(null);

    if (this.captchaRef && this.captchaRef.resetCaptcha) {
      this.captchaRef.resetCaptcha();
    }
  }

  handleCaptchaSuccess(captchaResponse: string): void {
    console.log(`token reCaptcha capturado`);
  }

  handleCaptchaError(error: any): void {
    console.error(`Error en reCAPTCHA: ${error}`);
    this.form?.get('recaptcha')?.setErrors({ captchaFailed: true });
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  private filtrarDevices() {
    if (!this.devices) return;
    let search = this.devicesFiltroCtrl.value;
    if (!search) {
      this.devicesFiltrados.next(this.devices.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    this.devicesFiltrados.next(this.devices.filter(device => device.selectLabel.toLowerCase().indexOf(search) > -1));
  }

  private filtrarCommands() {
    if (!this.commands) return;
    let search = this.commandsFiltroCtrl.value;
    if (!search) {
      this.commandsFiltrados.next(this.commands.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    this.commandsFiltrados.next(
      this.commands.filter(command => command.selectLabel.toLowerCase().indexOf(search) > -1)
    );
  }

  private updateDynamicInput(commandLabel: string | null): void {
    if (this.form) {
      const dynamicInputControl = this.form.get('dynamicInput');
      const recaptchaControl = this.form.get('recaptcha');

      if (commandLabel) {
        dynamicInputControl?.enable();
        recaptchaControl?.enable();

        if (commandLabel.includes('ping') && !commandLabel.includes('ipv6')) {
          this.dynamicInputLabel = 'Hostname';
        } else if (commandLabel.includes('traceroute') && !commandLabel.includes('ipv6')) {
          this.dynamicInputLabel = 'Hostname';
        } else if (commandLabel.includes('bgp route') && !commandLabel.includes('ipv6')) {
          this.dynamicInputLabel = 'Red';
        } else if (commandLabel.includes('ipv6')) {
          this.dynamicInputLabel = 'ipv6';
        } else {
          this.dynamicInputLabel = 'Input Adicional';
        }
      } else {
        dynamicInputControl?.disable();
        dynamicInputControl?.setValue('');
        this.dynamicInputLabel = '';

        recaptchaControl?.disable();
        recaptchaControl?.setValue('');
      }
      dynamicInputControl?.updateValueAndValidity();
      recaptchaControl?.updateValueAndValidity();
    }
  }

  openUsagePolicyDialog(event: MouseEvent): void {
    event.preventDefault();

    this.dialog.open(PoliticasUsoPopupComponent, {
      maxWidth: '600px',
      disableClose: false,
    });
  }
}
